import { usePreparedSchedule } from 'screens/Schedule/usePreparedSchedule'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useScheduleStatus } from 'screens/Schedule/useScheduleStatus'
import { StatusPill } from 'screens/Schedule/ScheduleStatusBanner/StatusPill'
import { pluralizeWithNumber } from 'utils'
import { useCrossPostAccountsToConnectCount } from 'hooks/useCrossPostAccountsToConnectCount'
import { ReadyStatusPill } from 'screens/Schedule/ScheduleStatusBanner/ReadyStatusPill'
import { ScheduleStatusPillPosting } from './ScheduleStatusPillPosting'
import { WarningCircle } from './Banners/WarningCircle'
import Styles from './Banners/ScheduleStatusBanner.module.scss'

dayjs.extend(utc)

interface ScheduleStatusPillProps {
  scheduleId: number
}

export const ScheduleStatusPill = ({ scheduleId }: ScheduleStatusPillProps) => {
  const { data: schedule } = usePreparedSchedule({ scheduleId })
  const { accountsToConnectCount } = useCrossPostAccountsToConnectCount(schedule?.tactics ?? [])
  const scheduleStatus = useScheduleStatus({ scheduleId })

  const pastStartDate = dayjs.utc(schedule?.start_date).isBefore(dayjs.utc())
  const unreviewedContentsCount = schedule?.unreviewed_contents_count ?? 0

  if (!schedule) return null

  switch (scheduleStatus) {
    case 'review':
      if (pastStartDate) {
        return <StatusPill status="review">Review Overdue</StatusPill>
      }
      return <StatusPill status="review">{pluralizeWithNumber(unreviewedContentsCount, 'post')} to Review</StatusPill>
    case 'approved':
      return (
        <div className={Styles.pillWithWarningCircle}>
          <StatusPill status="approved">Approved</StatusPill>
          {accountsToConnectCount > 0 && <WarningCircle schedule={schedule} />}
        </div>
      )
    case 'posted':
      return <StatusPill status="posted">Posted</StatusPill>
    case 'posting':
      return (
        <div className={Styles.pillWithWarningCircle}>
          <ScheduleStatusPillPosting startDate={schedule.startDate} />
          {accountsToConnectCount > 0 && <WarningCircle schedule={schedule} />}
        </div>
      )
    case 'paused':
      return <StatusPill status="paused">Paused</StatusPill>
    case 'failed':
      return <StatusPill status="failed">Failed</StatusPill>
    case 'ready':
      return <ReadyStatusPill approvalDate={schedule.approvalDate} />
    case 'generating':
      return <StatusPill status="generating">Generating now...</StatusPill>
    case 'rescheduled':
      return <StatusPill status="failed">Rescheduled</StatusPill>
  }
}
